import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError, explain } from '@/shared/api/problem'
import {
  createIncident, getIncident, patchIncident, submitIncident,
  type Incident, type IncidentPatch,
} from '../api'

/**
 * The report wizard's state.
 *
 * ## The draft is a real server row, not browser state
 *
 * On leaving step 1 with a category and a title, `POST /incidents` creates a DRAFT and
 * this hook holds its id. Every later step PATCHes. All the consequences are wanted:
 *
 * - closing the tab loses nothing, and the register offers "Draft — resume"
 * - evidence can be attached from step 6, because the incident already has an id
 * - a live feed can be opened from step 6, for the same reason
 * - an abandoned draft is deletable by its author and invisible to everybody else
 *
 * A `localStorage` draft would have none of those properties and would additionally be
 * lost the moment the reporter switches from the phone they filed on.
 *
 * ## Why submit is not pre-validated here
 *
 * `POST /incidents/{id}/submit` returns a 422 naming what is missing, and this hook maps
 * that back onto the earliest offending step. Re-implementing those rules in the client
 * would give two answers that can disagree — and the client's would be the wrong one, in
 * a feature whose whole point is a record that survives being asked about. Offer the
 * button; let the refusal speak.
 */

/** Which fields each step owns, so a 422 can name the step to jump back to. */
export const STEP_FIELDS: Record<number, string[]> = {
  1: ['categoryCode', 'category', 'subcategory', 'title', 'occurredAt', 'occurredAtPrecisionCode'],
  2: ['description', 'severityCode', 'severity', 'priorityCode', 'priority'],
  3: ['unitId', 'latitude', 'longitude', 'locationSourceCode', 'locationAccuracyM',
    'locationAbsentReason', 'locationLabel', 'location'],
  4: ['victimCount', 'injuryCount', 'deathCount', 'arrestsMade', 'estimatedVoterImpact'],
  5: ['perpetrator', 'perpetratorPartyId', 'relatedCandidateId', 'relatedPartyId',
    'sourceOutlet', 'sourceUrl', 'sourcePublishedAt', 'mediaCoverageCode'],
  6: ['tags', 'evidence'],
  7: ['iebcComplaintRef', 'dciCaseRef', 'courtCaseRef', 'ncicRef'],
}

export const TOTAL_STEPS = 7

/** Steps a field reporter may walk straight past. The rail must say so. */
export const SKIPPABLE_STEPS = new Set([4, 5, 6])

export interface DraftState {
  incident: Incident | null
  step: number
  saving: boolean
  error: string | null
  /** Server-named field → message, from a 422. Cleared on the next successful save. */
  fieldErrors: Record<string, string>
}

export function useReportDraft(resumeId: string | null) {
  const [incident, setIncident] = useState<Incident | null>(null)
  const [step, setStep] = useState(1)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(Boolean(resumeId))

  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  /**
   * The draft we already hold, readable from the resume effect without making that effect
   * depend on it — see the comment below for why it must not.
   */
  const held = useRef<Incident | null>(null)
  held.current = incident

  /*
     `resumeId` is null on a fresh report and a uuid when the register said "resume". It is
     LISTED as a dependency deliberately: with `[]` the effect would bind to the null it
     saw on first render and a resumed draft would open blank.

     The guard below is the other half of that. After step 1 creates the row, the wizard
     writes the new id into the URL so a reload resumes instead of starting a second draft
     — which changes `resumeId` and re-runs this effect on the draft we are already
     editing. Without the guard that refetch calls `firstUnansweredStep`, which answers
     "step 3" for any draft with a category, a title and a defaulted severity, and the
     reporter is thrown from step 1 straight past Narrative on their first Next.
  */
  useEffect(() => {
    if (!resumeId) { setIncident(null); setStep(1); setLoading(false); return }
    if (resumeId === held.current?.id) { setLoading(false); return }
    setLoading(true)
    getIncident(resumeId)
      .then((i) => {
        if (!alive.current) return
        setIncident(i)
        // Drop the reporter back where the draft ran out of answers rather than at step 1.
        setStep(firstUnansweredStep(i))
      })
      .catch((err) => { if (alive.current) setError(explain(err)) })
      .finally(() => { if (alive.current) setLoading(false) })
  }, [resumeId])

  /**
   * Create on the first save, PATCH on every one after.
   *
   * Returns the incident so a caller can chain (attach evidence needs the id), or null if
   * the save was refused — in which case `error`/`fieldErrors` carry the reason.
   */
  const save = useCallback(async (patch: IncidentPatch): Promise<Incident | null> => {
    setSaving(true)
    setError(null)
    try {
      let next: Incident
      if (!incident) {
        const { categoryCode, title, ...rest } = patch
        if (!categoryCode || !title) {
          // Not a client-side validation rule standing in for the server's: `POST` needs
          // these two to exist at all, so there is nothing to send yet. Step 1's Next
          // button is what enforces it, and it says which field is missing.
          setSaving(false)
          return null
        }
        next = await createIncident({ categoryCode, title, ...rest })
      } else {
        next = await patchIncident(incident.id, patch)
      }
      if (!alive.current) return next
      setIncident(next)
      setFieldErrors({})
      return next
    } catch (err) {
      if (alive.current) {
        setError(explain(err))
        if (err instanceof ApiError) setFieldErrors(err.fieldErrors())
      }
      return null
    } finally {
      if (alive.current) setSaving(false)
    }
  }, [incident])

  /**
   * Submit. On a 422 the wizard jumps to the earliest offending step and marks the field.
   *
   * A refusal that names no field at all still moves nothing and shows its sentence —
   * silently staying on step 7 with a red strip is the failure mode this avoids.
   */
  const submit = useCallback(async (): Promise<Incident | null> => {
    if (!incident) return null
    setSaving(true)
    setError(null)
    try {
      const done = await submitIncident(incident.id)
      if (alive.current) { setIncident(done); setFieldErrors({}) }
      return done
    } catch (err) {
      if (!alive.current) return null
      setError(explain(err))
      if (err instanceof ApiError) {
        const fields = err.fieldErrors()
        setFieldErrors(fields)
        const target = earliestStepFor(Object.keys(fields), err.problem.detail ?? '')
        if (target) setStep(target)
      }
      return null
    } finally {
      if (alive.current) setSaving(false)
    }
  }, [incident])

  /** Re-read after evidence or tags changed the row underneath the wizard. */
  const refresh = useCallback(async () => {
    if (!incident) return
    try {
      const next = await getIncident(incident.id)
      if (alive.current) setIncident(next)
    } catch (err) {
      if (alive.current) setError(explain(err))
    }
  }, [incident])

  return {
    incident, step, setStep, saving, loading, error, setError, fieldErrors,
    save, submit, refresh,
  }
}

/** Where a resumed draft ran out of answers. Never past a step that is still unanswered. */
function firstUnansweredStep(i: Incident): number {
  if (!i.categoryCode || !i.title) return 1
  if (!i.severityCode) return 2
  const hasLocation = i.unitId || i.latitude != null || i.locationAbsentReason
  if (!hasLocation) return 3
  // Steps 4–6 are skippable, so a draft that got past location resumes at review — the
  // reporter can still walk back, and the rail says which steps they skipped.
  return 7
}

/**
 * The earliest step that owns any refused field.
 *
 * Falls back to scanning the problem's own sentence for a step's field names, because the
 * completeness refusal is one prose `detail` ("a location is required…") rather than a
 * per-field list. Returns null when nothing matches, and the caller then leaves the
 * reporter where they are with the message shown.
 */
function earliestStepFor(fields: string[], detail: string): number | null {
  const haystack = detail.toLowerCase()
  for (let s = 1; s <= TOTAL_STEPS; s++) {
    const owned = STEP_FIELDS[s] ?? []
    if (fields.some((f) => owned.includes(f))) return s
    if (owned.some((f) => haystack.includes(f.toLowerCase()))) return s
  }
  return null
}
